/**
 * Estilos da Capacidade da Fábrica, no mesmo desenho dos Relatórios do
 * Legado: lista com divisórias (não mural de cartões), texto neutro e UM
 * sinal de cor por linha, o ponto ao lado da situação. Nada de rolagem
 * lateral: colunas com `minmax(0, …)` e reticências.
 *
 * Abaixo de 1024px as listas viram os cartões de 3 linhas da ListaCompacta
 * (a página troca o componente; aqui ficam só os ajustes).
 */
import styled from "styled-components";

import { PageContainer } from "../../styles/shared";
import { BP_MOBILE, BP_TABLET } from "../../styles/canonical/breakpoints";

const tabletAcima = `(min-width: ${BP_MOBILE + 1}px)`;
const desktop = `(min-width: ${BP_TABLET + 1}px)`;

const tom = (nome, parte) => `var(--${nome || "neutral"}-${parte})`;

export const Pagina = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 12px 12px 32px;

  @media ${tabletAcima} {
    padding: 16px 20px 40px;
    gap: 16px;
  }
`;

// --------------------------------------------------------------------- topo

export const Topo = styled.div`
  position: sticky;
  top: var(--app-header-height, 0px);
  z-index: 80;
  margin: -12px -12px 0;
  padding: 10px 12px;
  background: ${({ theme }) => theme.surface};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  @media ${tabletAcima} {
    margin: -16px -20px 0;
    padding: 12px 20px;
  }
`;

export const LinhaTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const BlocoTitulo = styled.div`
  flex: 1 1 auto;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 700;
    line-height: 1.2;
    color: ${({ theme }) => theme.textPrimary};
  }

  p {
    margin: 1px 0 0;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-variant-numeric: tabular-nums;
  }

  @media ${desktop} {
    h1 {
      font-size: 20px;
    }
  }
`;

export const Controles = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
`;

/* Seletor de opções (abas e escala): uma pílula com o item ativo elevado. */
export const Segmentado = styled.div`
  display: inline-flex;
  /* Cinco abas não cabem lado a lado no celular: quebram em duas linhas
     em vez de virar "Vendas a…" ou empurrar a página para o lado. */
  flex-wrap: wrap;
  max-width: 100%;
  padding: 2px;
  border-radius: 9px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};

  button {
    flex: 1 1 auto;
    min-width: 0;
    border: 0;
    background: transparent;
    padding: 0 10px;
    min-height: 32px;
    border-radius: 7px;
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.textSecondary};
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  button[aria-pressed="true"],
  button[aria-selected="true"] {
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    box-shadow: var(--shadow-1, 0 1px 2px rgba(0, 0, 0, 0.08));
  }

  button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }
`;

export const LinhaControles = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

// ------------------------------------------------------------- indicadores

/* O ÚNICO lugar em que o estado ganha cor. */
export const Ponto = styled.i`
  flex: 0 0 auto;
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ $tom }) => tom($tom, "text")};
`;

export const Indicadores = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;

  @media ${desktop} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const Indicador = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 10px 12px;
  border-right: 1px solid ${({ theme }) => theme.border};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  margin: 0 -1px -1px 0;

  .rot {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
  }

  .num {
    font-size: 18px;
    font-weight: 650;
    line-height: 1.15;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.textPrimary};
    overflow-wrap: anywhere;
  }

  .comp {
    font-size: var(--text-min, 12px);
    font-weight: 600;
    color: ${({ theme }) => theme.textPrimary};
    font-variant-numeric: tabular-nums;
  }

  .regua {
    font-size: 11.5px;
    line-height: 1.35;
    color: ${({ theme }) => theme.textTertiary};
  }

  @media ${tabletAcima} {
    padding: 12px 16px;

    .num {
      font-size: 20px;
    }
  }
`;

/* Aviso neutro em texto (motivo de não haver ritmo, planilha parada…). */
export const Aviso = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  line-height: 1.45;

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
    color: ${({ $tom }) => tom($tom, "text")};
  }

  a {
    color: ${({ theme }) => theme.accent};
    font-weight: 600;
  }

  strong {
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const Regua = styled.p`
  margin: 0;
  padding: 0 2px;
  font-size: var(--text-min, 12px);
  line-height: 1.45;
  color: ${({ theme }) => theme.textTertiary};
`;

// -------------------------------------------------------------------- lista

export const Bloco = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  h2 {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0;
    padding: 0 2px;
    font-size: 13px;
    font-weight: 650;
    color: ${({ theme }) => theme.textPrimary};
  }

  h2 span {
    font-weight: 500;
    color: ${({ theme }) => theme.textTertiary};
    font-variant-numeric: tabular-nums;
  }
`;

/* Um contêiner só, linhas separadas por divisória. */
export const Lista = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
  min-width: 0;
`;

/* Lista de ambientes solta (sem cabeçalho de colunas): só um recuo interno. */
export const ListaRecuada = styled(Lista)`
  padding: 4px 14px;
`;

export const ItemLista = styled.div`
  min-width: 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.border};
  }
`;

const colunasPeriodo = "minmax(0, 1.2fr) 90px minmax(0, 1fr) minmax(0, 1.3fr) 16px";

export const CabecalhoPeriodo = styled.button`
  display: grid;
  grid-template-columns: ${colunasPeriodo};
  column-gap: 16px;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 12px 18px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  font-variant-numeric: tabular-nums;

  > span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .periodo {
    font-weight: 650;
  }

  .du,
  .produzido {
    color: ${({ theme }) => theme.textSecondary};
  }

  .ocupacao {
    font-weight: 600;
  }

  .situacao {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }

  .seta {
    color: ${({ theme }) => theme.textTertiary};
    transition: transform 0.16s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .seta[data-aberto="sim"] {
    transform: rotate(180deg);
  }

  &.colunas {
    cursor: default;
    padding: 9px 18px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(.colunas):hover {
      background: ${({ theme }) => theme.surfaceAlt};
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .seta {
      transition: none;
    }
  }
`;

/* Conteúdo aberto de uma linha (clientes, camadas, potencial, PCP). */
export const Detalhe = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 14px 14px;
  background: ${({ theme }) => theme.surfaceAlt};
  border-top: 1px solid ${({ theme }) => theme.border};
  min-width: 0;

  @media ${desktop} {
    padding: 12px 18px 16px 30px;
  }

  h3 {
    margin: 0 0 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }

  p {
    margin: 0;
    font-size: var(--text-min, 12px);
    line-height: 1.45;
    color: ${({ theme }) => theme.textSecondary};
  }
`;

export const ListaSimples = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/* Linha de cliente/ambiente: nome à esquerda, dados alinhados à direita. */
export const LinhaItem = styled.li`
  min-width: 0;

  > button,
  > div {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    column-gap: 10px;
    row-gap: 1px;
    align-items: baseline;
    width: 100%;
    min-width: 0;
    padding: 6px 0;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    text-align: left;
  }

  > button {
    cursor: pointer;
  }

  > button:disabled {
    cursor: default;
  }

  > button:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }

  & + & {
    border-top: 1px dashed ${({ theme }) => theme.border};
  }

  .nome {
    min-width: 0;
    font-size: 13px;
    font-weight: 550;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .meta {
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    text-align: right;
  }

  .sub {
    grid-column: 1 / -1;
    min-width: 0;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textTertiary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Selo em texto, sem fundo: "pode atrasar" leva a cor do estado. */
export const Selo = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.border};
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  color: ${({ theme, $tom }) => ($tom ? tom($tom, "text") : theme.textSecondary)};
`;

export const Camadas = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 14px;
  row-gap: 2px;
  font-size: var(--text-min, 12px);
  color: ${({ theme }) => theme.textSecondary};
  font-variant-numeric: tabular-nums;

  b {
    font-weight: 650;
    color: ${({ theme }) => theme.textPrimary};
  }
`;

export const Vazio = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 12px;
`;

export const BotaoTexto = styled.button`
  align-self: flex-start;
  border: 0;
  background: none;
  padding: 4px 2px;
  min-height: 32px;
  font: inherit;
  font-size: var(--text-min, 12px);
  font-weight: 600;
  color: ${({ theme }) => theme.accent};
  cursor: pointer;
`;

// ------------------------------------------------------------ formulários

export const Campos = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  min-width: 0;

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
    font-size: var(--text-min, 12px);
    font-weight: 600;
    color: ${({ theme }) => theme.textSecondary};
  }

  input {
    min-height: 36px;
    max-width: 100%;
    padding: 5px 10px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.border};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
    font: inherit;
    /* 16px no celular: abaixo disso o iOS dá zoom ao focar. */
    font-size: 16px;
  }

  @media ${tabletAcima} {
    input {
      font-size: 13px;
    }
  }
`;

/* Tabela sem <table>: linhas em grade com colunas fixas por página. */
export const Grade = styled.div`
  display: grid;
  grid-template-columns: ${({ $colunas }) => $colunas};
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surface};
  overflow: hidden;
  font-size: var(--text-min, 12px);
  font-variant-numeric: tabular-nums;

  > span {
    min-width: 0;
    padding: 8px 12px;
    border-top: 1px solid ${({ theme }) => theme.border};
    color: ${({ theme }) => theme.textSecondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > span.cab {
    border-top: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textTertiary};
  }

  > span.forte {
    color: ${({ theme }) => theme.textPrimary};
    font-weight: 600;
  }
`;

/* Nome de ambiente dentro de uma linha de cliente: abre o painel lateral. */
export const LinkAmbiente = styled.button`
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.border};
  text-underline-offset: 2px;

  &:disabled {
    cursor: default;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

/* Linha da aba Qualidade: nível (ponto + texto), problema, quantidade. */
export const CabecalhoQualidade = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 16px;
  grid-template-areas:
    "nivel qtd seta"
    "rotulo rotulo seta";
  column-gap: 12px;
  row-gap: 2px;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 11px 14px;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  .nivel {
    grid-area: nivel;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: var(--text-min, 12px);
    color: ${({ theme }) => theme.textSecondary};
  }

  .rotulo {
    grid-area: rotulo;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .qtd {
    grid-area: qtd;
    font-weight: 650;
    font-variant-numeric: tabular-nums;
  }

  .seta {
    grid-area: seta;
    color: ${({ theme }) => theme.textTertiary};
  }

  .seta[data-aberto="sim"] {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: -2px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:not(:disabled):hover {
      background: ${({ theme }) => theme.surfaceAlt};
    }
  }

  @media ${desktop} {
    grid-template-columns: 110px minmax(0, 1fr) 56px 16px;
    grid-template-areas: "nivel rotulo qtd seta";
    padding: 11px 18px;

    .qtd {
      text-align: right;
    }
  }
`;
